import { create } from 'zustand';
import { stateService } from '../core/StateService.js';

// Hilfsfunktion für sichere Float-Berechnungen
const round2 = (num) => Math.round((parseFloat(num) || 0) * 100) / 100;
const mwstFactor = 1.19;
const toBrutto = (netto) => round2(netto * mwstFactor);

// Standard-Struktur einer neuen Berechnung (Session)
const createEmptySession = (id = Date.now().toString()) => ({
  id,
  name: 'Neue Berechnung',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  
  kundenName: '',
  vnPrefix: '',
  vnNummer: '',
  belegDatum: new Date().toISOString().split('T')[0],

  articles: [], 
  hardwareZuschussBrutto: '',
  tariffs: [],
  pushNetto: '',
  pushBrutto: '',
  redbullNetto: '',
  getraenkeNetto: '',
  getraenkeBrutto: '',
  getraenkeList: [],
  individuellNetto: '',
  individuellBezeichnung: ''
});

export const useSpielgeldStore = create((set, get) => ({
      // === GLOBAL STATE ===
      globalBudget: 0,
      setGlobalBudget: (amount) => {
        set({ globalBudget: parseFloat(amount) || 0 });
        get()._saveSettings();
      },
      defaultRedbullPreis: '2.49',
      setDefaultRedbullPreis: (price) => {
        set({ defaultRedbullPreis: price });
        get()._saveSettings();
      },
      topUps: [],
      markets: [],
      dbTariffs: [],
      dbOptions: [],
      isInitialized: false,

      _saveSettings: () => {
        const st = get();
        stateService.saveSpielgeldSettings({ 
          globalBudget: st.globalBudget, 
          defaultRedbullPreis: st.defaultRedbullPreis, 
          topUps: st.topUps 
        });
      },

      // Initialisiert den Store asynchron aus der PostgreSQL/SQLite DB
      initFromDB: async () => {
        if (get().isInitialized) return;
        get().fetchCatalog(); // Lade den Katalog für Tarife und Optionen
        
        // Settings laden
        const settings = stateService.getSpielgeldSettings();
        if (settings) {
            set({
                globalBudget: settings.globalBudget !== undefined ? settings.globalBudget : 0,
                defaultRedbullPreis: settings.defaultRedbullPreis !== undefined ? settings.defaultRedbullPreis : '2.49',
                topUps: settings.topUps || []
            });
        }

        try {
            const dbSessions = await stateService.fetchSessions();
            if (dbSessions && dbSessions.length > 0) {
                set({ sessions: dbSessions, activeSessionId: dbSessions[0].id, isInitialized: true });
            } else {
                set({ isInitialized: true });
            }
        } catch(e) {
            console.error('Init Error', e);
            set({ isInitialized: true }); // Fallback
        }
      },

      fetchMarkets: async () => {
        try {
          const res = await fetch('/api/stores');
          const data = await res.json();
          if (data && data.stores) set({ markets: data.stores });
        } catch (err) {
          console.error('Failed to fetch markets', err);
        }
      },

      fetchCatalog: async () => {
        try {
          const resT = await fetch('/api/tariffs');
          const dataT = await resT.json();
          if (dataT && Array.isArray(dataT)) set({ dbTariffs: dataT });
          
          const resO = await fetch('/api/options');
          const dataO = await resO.json();
          if (dataO && Array.isArray(dataO)) set({ dbOptions: dataO });
        } catch (err) {
          console.error('Failed to fetch catalog', err);
        }
      },
      addTopUp: (amount, description) => {
        set((state) => ({
          topUps: [{ id: Date.now().toString(), amount: parseFloat(amount) || 0, description, date: new Date().toISOString() }, ...state.topUps]
        }));
        get()._saveSettings();
      },
      removeTopUp: (id) => {
        set((state) => ({
          topUps: state.topUps.filter(t => t.id !== id)
        }));
        get()._saveSettings();
      },

      // === SESSION MANAGEMENT ===
      sessions: [createEmptySession('default-session')],
      activeSessionId: 'default-session',

      getActiveSession: () => {
        const { sessions, activeSessionId } = get();
        return sessions.find(s => s.id === activeSessionId) || sessions[0];
      },

      createNewSession: () => {
        const newSession = createEmptySession();
        // Asynchron speichern
        stateService.saveSession(newSession).catch(e => console.error(e));
        set((state) => ({
          sessions: [newSession, ...state.sessions],
          activeSessionId: newSession.id
        }));
      },

      switchSession: (id) => set({ activeSessionId: id }),

      deleteSession: (id) => {
        // Asynchron löschen
        stateService.deleteSession(id).catch(e => console.error(e));
        
        set((state) => {
            const newSessions = state.sessions.filter(s => String(s.id) !== String(id));
            if (newSessions.length === 0) {
              const fresh = createEmptySession();
              stateService.saveSession(fresh).catch(e=>console.error(e));
              return { sessions: [fresh], activeSessionId: fresh.id };
            }
            return { 
              sessions: newSessions,
              activeSessionId: String(state.activeSessionId) === String(id) ? newSessions[0].id : state.activeSessionId
            };
        });
      },

      // Aktualisiert Daten in der AKTIVEN Session und speichert in der DB
      updateActiveSession: (updates) => {
        set((state) => {
            let updatedSessionRef = null;
            const newSessions = state.sessions.map(session => {
              if (session.id === state.activeSessionId) {
                const updated = { ...session, ...updates, updatedAt: new Date().toISOString() };
                updatedSessionRef = updated;
                return updated;
              }
              return session;
            });

            // Fire & Forget ans Backend
            if (updatedSessionRef) {
                stateService.saveSession(updatedSessionRef).catch(e => {
                    console.error('Save failed', e);
                });
            }

            return { sessions: newSessions };
        });
      },

      // === BERECHNUNGS-HELFER ===
      
      addArticle: () => {
        const active = get().getActiveSession();
        if (active.articles.length >= 6) return; 
        const newArticle = { id: Date.now().toString(), category: 'smartphone', name: '', preis: '', inputMode: 'brutto', bild: '' };
        get().updateActiveSession({ articles: [...active.articles, newArticle] });
      },

      updateArticle: (id, updates) => {
        const active = get().getActiveSession();
        const updatedArticles = active.articles.map(a => a.id === id ? { ...a, ...updates } : a);
        get().updateActiveSession({ articles: updatedArticles });
      },

      removeArticle: (id) => {
        const active = get().getActiveSession();
        get().updateActiveSession({ articles: active.articles.filter(a => a.id !== id) });
      },

      addGetraenk: (defaultBrutto = '') => {
        const active = get().getActiveSession();
        const list = active.getraenkeList || [];
        const n = defaultBrutto ? (parseFloat(defaultBrutto)/1.19).toFixed(2) : '';
        const newGetraenk = { id: Date.now().toString(), name: defaultBrutto ? 'Redbull' : '', anzahl: 1, netto: n, brutto: defaultBrutto, contractSigned: false };
        get().updateActiveSession({ getraenkeList: [...list, newGetraenk] });
      },

      updateGetraenk: (id, updates) => {
        const active = get().getActiveSession();
        const list = active.getraenkeList || [];
        get().updateActiveSession({ getraenkeList: list.map(g => g.id === id ? { ...g, ...updates } : g) });
      },

      removeGetraenk: (id) => {
        const active = get().getActiveSession();
        const list = active.getraenkeList || [];
        get().updateActiveSession({ getraenkeList: list.filter(g => g.id !== id) });
      },

      updateTariffList: (tariffs) => {
        get().updateActiveSession({ tariffs });
      },
      addTariff: () => {
        const active = get().getActiveSession();
        if (active.tariffs.length >= 4) return;
        const newTariff = { id: Date.now().toString(), name: '', zusatzCouponNetto: '', couponErhohungBrutto: '' };
        get().updateActiveSession({ tariffs: [...active.tariffs, newTariff] });
      },
      updateTariff: (id, updates) => {
        const active = get().getActiveSession();
        get().updateActiveSession({ tariffs: active.tariffs.map(t => t.id === id ? { ...t, ...updates } : t) });
      },
      removeTariff: (id) => {
        const active = get().getActiveSession();
        get().updateActiveSession({ tariffs: active.tariffs.filter(t => t.id !== id) });
      },

      calculateSessionTotal: (session) => {
        if (!session) return 0;
        let totalUsed = 0;
        
        if (session.articles) {
            session.articles.forEach(a => {
              const val = parseFloat(a.preis) || 0;
              totalUsed += a.inputMode === 'brutto' ? val : toBrutto(val);
            });
        }
        totalUsed += parseFloat(session.hardwareZuschussBrutto) || 0;
        
        if (session.tariffs) {
            session.tariffs.forEach(t => {
              totalUsed += parseFloat(t.couponBrutto) || 0;
              totalUsed += parseFloat(t.couponErhohungBrutto) || 0;
            });
        }

        if (session.pushBrutto) totalUsed += parseFloat(session.pushBrutto);
        else if (session.pushNetto) totalUsed += toBrutto(session.pushNetto);

        if (session.redbullNetto) totalUsed += toBrutto(session.redbullNetto);
        
        if (session.getraenkeBrutto) totalUsed += parseFloat(session.getraenkeBrutto);
        else if (session.getraenkeNetto) totalUsed += toBrutto(session.getraenkeNetto);
        
        if (session.getraenkeList && session.getraenkeList.length > 0) {
          session.getraenkeList.forEach(g => {
            if (g.brutto) totalUsed += parseFloat(g.brutto);
            else if (g.netto) totalUsed += toBrutto(g.netto);
          });
        }
        
        if (session.individuellNetto) totalUsed += toBrutto(session.individuellNetto);
        
        return round2(totalUsed);
      },

      calculateMonthlyWallet: () => {
        const { globalBudget, topUps, sessions, calculateSessionTotal } = get();
        const totalTopUps = topUps.reduce((sum, t) => sum + t.amount, 0);
        const totalAvailable = globalBudget + totalTopUps;
        const totalUsedAllSessions = sessions.reduce((sum, s) => sum + calculateSessionTotal(s), 0);
        const currentBalance = totalAvailable - totalUsedAllSessions;
        const usedPercent = totalAvailable > 0 ? (totalUsedAllSessions / totalAvailable) * 100 : 0;

        return {
          baseBudget: globalBudget,
          totalTopUps,
          totalAvailable,
          totalUsedAllSessions,
          currentBalance,
          usedPercent
        };
      },

      calculateTotals: () => {
        const active = get().getActiveSession();
        const { globalBudget } = get();
        let totalAdBrutto = 0;
        let totalMarktBrutto = 0;
        let totalGetraenkeBrutto = 0;

        if (active.articles) {
            active.articles.forEach(a => {
              const val = parseFloat(a.preis) || 0;
              if (a.inputMode === 'brutto') totalAdBrutto += val;
              else totalAdBrutto += toBrutto(val);
            });
        }
        totalAdBrutto += parseFloat(active.hardwareZuschussBrutto) || 0;

        if (active.tariffs) {
            active.tariffs.forEach(t => {
              totalMarktBrutto += parseFloat(t.couponBrutto) || 0;
              totalMarktBrutto += parseFloat(t.couponErhohungBrutto) || 0;
            });
        }

        if (active.pushBrutto) totalMarktBrutto += parseFloat(active.pushBrutto);
        else if (active.pushNetto) totalMarktBrutto += toBrutto(active.pushNetto);

        if (active.redbullNetto) totalGetraenkeBrutto += toBrutto(active.redbullNetto);
        if (active.getraenkeBrutto) totalGetraenkeBrutto += parseFloat(active.getraenkeBrutto);
        else if (active.getraenkeNetto) totalGetraenkeBrutto += toBrutto(active.getraenkeNetto);

        if (active.getraenkeList && active.getraenkeList.length > 0) {
          active.getraenkeList.forEach(g => {
            if (g.brutto) totalGetraenkeBrutto += parseFloat(g.brutto);
            else if (g.netto) totalGetraenkeBrutto += toBrutto(g.netto);
          });
        }
        
        if (active.individuellNetto) totalMarktBrutto += toBrutto(active.individuellNetto);

        const totalUsed = round2(totalAdBrutto + totalMarktBrutto + totalGetraenkeBrutto);
        const budgetRemaining = round2(globalBudget - totalUsed);
        const budgetPercent = globalBudget > 0 ? (totalUsed / globalBudget) * 100 : 0;

        return {
          totalAdBrutto,
          totalMarktBrutto,
          totalGetraenkeBrutto,
          totalUsed,
          budgetRemaining,
          budgetPercent
        };
      }
}));
